import { createRouter, createWebHistory } from 'vue-router';
import type { LocationQuery, LocationQueryRaw, RouteRecordRaw } from 'vue-router';
import qs from 'qs';

import { setI18nLanguage } from '@/plugins/i18n';
import { RequestParsingService } from '@/views/ibis/requestParsing.service';

import Identify from '@/views/ibis/Identify.vue';
import Callback from '@/views/debugger/Callback.vue';
import IbisLogin from '@/views/ibis/login/Login.vue';
import IbisLogout from '@/views/ibis/Logout.vue';
import CorrelationFailed from '@/views/ibis/CorrelationFailed.vue';
import MtlsFailed from '@/views/cassowary/MtlsFailed.vue';
import Debugger from '@/views/debugger/Debugger.vue';
import UiGallery from '@/views/debugger/UiGallery.vue';
import KookaburraIdentify from '@/views/kookaburra/Identify.vue';
import KookaburraLogin from '@/views/kookaburra/Login.vue';
import KookaburraError from '@/views/kookaburra/OtpError.vue';
import ElvisLogin from '@/views/elvis/login.vue';
import ElvisRegister from '@/views/elvis/register.vue';
import ElvisForgotUsername from '@/views/elvis/forgot/username.vue';
import ElvisForgotPassword from '@/views/elvis/forgot/password.vue';
import ElvisSetPassword from '@/views/elvis/setNewPassword.vue';
import SignUp from '@/views/ibis/sign-up/SignUp.vue';
import VerifyUser from '@/views/ibis/sign-up/VerifyUser.vue';
import NationalIdInfo from '@/views/ibis/NationalIdInfo.vue';
import CancelWarning from '@/views/ibis/CancelWarning.vue';
import Validate from '@/views/ibis/sign-up/Validate.vue';
import StepUp from '@/views/ibis/step-up/StepUp.vue';
import Activate from '@/views/ibis/device-flow/Activate.vue';
import DeviceCallback from '@/views/ibis/device-flow/Consent.vue';
import DeviceLogin from '@/views/ibis/device-flow/Login.vue';
import DeviceSuccess from '@/views/ibis/device-flow/Success.vue';
import DeviceFailure from '@/views/ibis/device-flow/Failure.vue';

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'identify', component: Identify, props: true },
  {
    path: '/ibis/national-id-info',
    name: 'ibis-national-id-info',
    component: NationalIdInfo,
    props: true,
  },
  { path: '/ibis/sign-up', name: 'ibis-sign-up-phone', component: SignUp, props: true },
  { path: '/ibis/sign-up/person', name: 'ibis-sign-up-person', component: SignUp, props: true },
  { path: '/ibis/sign-up/verify', name: 'ibis-sign-up-verify', component: VerifyUser, props: true },
  {
    path: '/ibis/sign-up/validate',
    name: 'ibis-sign-up-validate',
    component: Validate,
    props: true,
  },
  { path: '/ibis/step-up', name: 'ibis-step-up', component: StepUp, props: true },
  { path: '/ibis/login', name: 'ibis-login', component: IbisLogin, props: true },
  {
    path: '/ibis/cancel-warning-info',
    name: 'ibis-cancel-warning',
    component: CancelWarning,
    props: true,
  },
  { path: '/ibis/logout', name: 'ibis-logout', component: IbisLogout, props: true },
  {
    path: '/ibis/correlation-failed',
    name: 'correlation-failed',
    component: CorrelationFailed,
    props: true,
  },
  { path: '/cas/mtls-failed', name: 'mtls-failed', component: MtlsFailed, props: true },
  { path: '/debugger', name: 'debugger', component: Debugger, props: true },
  { path: '/debugger/ui', name: 'debugger-ui', component: UiGallery, props: true },
  { path: '/debugger/callback', name: 'oauth-callback', component: Callback, props: true },
  { path: '/koo/identify', name: 'koo-identify', component: KookaburraIdentify, props: true },
  { path: '/koo/login', name: 'koo-login', component: KookaburraLogin, props: true },
  { path: '/koo/error', name: 'koo-error', component: KookaburraError, props: true },
  { path: '/elv/login', name: 'elv-login', component: ElvisLogin, props: true },
  { path: '/elv/register', name: 'elv-register', component: ElvisRegister, props: true },
  {
    path: '/elv/forgot/username',
    name: 'elv-forgot-username',
    component: ElvisForgotUsername,
    props: true,
  },
  {
    path: '/elv/forgot/password',
    name: 'elv-forgot-password',
    component: ElvisForgotPassword,
    props: true,
  },
  {
    path: '/elv/forgot/set-password',
    name: 'elv-set-password',
    component: ElvisSetPassword,
    props: true,
  },
  { path: '/device/activate', name: 'Activate', component: Activate, props: true },
  { path: '/device/login', name: 'DeviceLogin', component: DeviceLogin, props: true },
  { path: '/device/callback', name: 'oath-callback', component: DeviceCallback, props: true },
  { path: '/device/success', name: 'Success', component: DeviceSuccess, props: true },
  { path: '/device/failure', name: 'Failure', component: DeviceFailure, props: true },
];

const router = createRouter({
  history: createWebHistory('/ui/'),
  routes,
  parseQuery: (search: string) => qs.parse(search) as unknown as LocationQuery,
  // NOTE: Vue Router 4+ appends the '?' itself (`path + (query && '?') + query`),
  // so - unlike Vue Router 3 - this must NOT return a leading '?'.
  stringifyQuery: (query: LocationQueryRaw | undefined) =>
    query ? qs.stringify(query, { encode: false }) : '',
});

router.beforeEach((to) => {
  const lsKey = 'ui_locales';
  const defaultLang = 'no';

  try {
    const uiLocales = to.query.ui_locales;
    let lang = window.localStorage.getItem(lsKey) || defaultLang;

    if (typeof uiLocales === 'string') {
      lang = uiLocales.split(' ', 1)[0];
    } else {
      const request = RequestParsingService.parseUrlEncodedJson(to.query.requestUrlEncoded);

      if (request && request.language) {
        lang = request.language;
      }
    }

    if (lang.includes(' ')) {
      lang = lang.split(' ')[0];
    }

    if (lang !== defaultLang) {
      window.localStorage.setItem(lsKey, lang);
    }

    console.debug(`Setting UI language to ${lang}`);
    setI18nLanguage(lang);
  } catch (error) {
    // Abort the navigation explicitly. Returning nothing here would resolve the
    // guard and continue; previously a thrown/unhandled branch left the
    // navigation pending forever because neither next() nor next(false) ran.
    console.error('Failed to resolve UI language for navigation', error);
    return false;
  }

  return true;
});

export default router;
